/**
 * EditEntityModal.js
 *
 * Admin edit form for business or vet profile fields. Routes through the
 * Cloud Function `adminUpdateEntityProfile`, which server-side filters
 * updates against a whitelist of safe fields:
 *
 *   business: businessName, description, phone, email, website, address,
 *             city, country, businessType
 *   vet:      firstName, lastName, clinicName, description, phone, email,
 *             address, city, country, specializations (array)
 *
 * Anything outside the whitelist is silently ignored on the server. The
 * modal only renders the editable fields for the chosen entityType.
 */

import React, { useEffect, useState } from 'react';
import { Modal, Button } from '../UI';
import { updateEntityProfile } from '../../services/adminService';

const BUSINESS_FIELDS = [
  { key: 'businessName',  label: 'Business name' },
  { key: 'description',   label: 'Description', textarea: true },
  { key: 'phone',         label: 'Phone' },
  { key: 'email',         label: 'Email' },
  { key: 'website',       label: 'Website' },
  { key: 'address',       label: 'Address' },
  { key: 'city',          label: 'City' },
  { key: 'country',       label: 'Country' },
  { key: 'businessType',  label: 'Business type' },
];

const VET_FIELDS = [
  { key: 'firstName',       label: 'First name' },
  { key: 'lastName',        label: 'Last name' },
  { key: 'clinicName',      label: 'Clinic name' },
  { key: 'description',     label: 'Description', textarea: true },
  { key: 'phone',           label: 'Phone' },
  { key: 'email',           label: 'Email' },
  { key: 'address',         label: 'Address' },
  { key: 'city',            label: 'City' },
  { key: 'country',         label: 'Country' },
  { key: 'specializations', label: 'Specializations (comma-separated)', isArray: true },
];

const EditEntityModal = ({ isOpen, onClose, entityType, entity, onSaved }) => {
  const fields = entityType === 'business' ? BUSINESS_FIELDS : VET_FIELDS;

  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Re-seed the form whenever a different entity is opened.
  useEffect(() => {
    if (!entity) { setValues({}); return; }
    const seed = {};
    for (const f of fields) {
      const raw = entity[f.key];
      seed[f.key] = f.isArray && Array.isArray(raw) ? raw.join(', ') : (raw ?? '');
    }
    setValues(seed);
    setError('');
  }, [entity, entityType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!entity) return;
    setSaving(true);
    setError('');

    // Build the diff: only send fields that actually changed, so we don't
    // overwrite untouched data with stale form state.
    const updates = {};
    for (const f of fields) {
      const formVal = values[f.key];
      const dbVal = entity[f.key];
      const dbCmp = f.isArray && Array.isArray(dbVal) ? dbVal.join(', ') : (dbVal ?? '');
      if (formVal === dbCmp) continue;

      if (f.isArray) {
        updates[f.key] = String(formVal || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        updates[f.key] = formVal;
      }
    }

    if (Object.keys(updates).length === 0) {
      setError('No changes to save.');
      setSaving(false);
      return;
    }

    try {
      await updateEntityProfile({
        entityType,
        entityId: entity.id,
        updates,
      });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit ${entityType === 'business' ? 'business' : 'vet'} profile`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>Save changes</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {fields.map((f) => (
          <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>{f.label}</span>
            {f.textarea ? (
              <textarea
                rows={3}
                value={values[f.key] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                style={{
                  background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6,
                  padding: '8px 10px', color: '#e8eaed', fontFamily: 'inherit', fontSize: 13,
                }}
              />
            ) : (
              <input
                type="text"
                value={values[f.key] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                style={{
                  background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6,
                  padding: '8px 10px', color: '#e8eaed', fontSize: 13,
                }}
              />
            )}
          </label>
        ))}
        {error && (
          <div style={{ color: '#f87171', fontSize: 13, marginTop: 4 }}>{error}</div>
        )}
      </div>
    </Modal>
  );
};

export default EditEntityModal;
